import React from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  CircularProgress,
  Collapse,
  Alert,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import { useLanguage } from "../../../contexts/LanguageContext";
import { MemoryFact, MemoryFactSource } from "../../../common/services/stream";
import { getConversationShortCode } from "../../../components/Chat/requests";
import { ROUTES } from "../../../common/constant/routes";
import {
  getPatientFacts,
  updatePatientFact,
  invalidatePatientFact,
  restorePatientFact,
  deletePatientFact,
  addPatientFact,
  getPatientMemoryStatus,
  FactStatusFilter,
} from "../requests";

type Props = { patientId: string | null };

// A fact is no longer in force once it has been invalidated or expired.
const isInvalidated = (f: MemoryFact) => Boolean(f.invalid_at || f.expired_at);

const formatDate = (iso?: string | null, locale = "en-US"): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric" }).format(d);
};

export default function FactsPanel({ patientId }: Props) {
  const { t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();

  // Jump to the chat turn a fact was extracted from: resolve the conversation's
  // shortCode (route is /c/:shortcode), then navigate with a #msg-<id> hash that
  // Chat.tsx scrolls to once that conversation's history renders. Mirrors the
  // click-through in the chat's MemorySources dialog.
  const jumpToSource = React.useCallback(
    async (source: MemoryFactSource) => {
      if (!source.conversation_id || !source.message_id) return;
      const shortCode = await getConversationShortCode(source.conversation_id);
      if (!shortCode) return;
      navigate(`${ROUTES.CONVERSATION}/${shortCode}#msg-${source.message_id}`);
    },
    [navigate]
  );

  const [facts, setFacts] = React.useState<MemoryFact[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [filter, setFilter] = React.useState<FactStatusFilter>("all");

  // Inline edit
  const [editingUuid, setEditingUuid] = React.useState<string | null>(null);
  const [editText, setEditText] = React.useState("");
  // Per-row action in flight (edit save / invalidate / restore / delete)
  const [busyUuid, setBusyUuid] = React.useState<string | null>(null);
  const [rowError, setRowError] = React.useState<string | null>(null);
  // Expanded source panel
  const [openSourceUuid, setOpenSourceUuid] = React.useState<string | null>(null);

  // Row action menu
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuFact, setMenuFact] = React.useState<MemoryFact | null>(null);

  // Confirmation dialog for the two destructive removals
  const [confirm, setConfirm] = React.useState<{ kind: "invalidate" | "delete"; fact: MemoryFact } | null>(null);

  // Add-fact dialog
  const [addOpen, setAddOpen] = React.useState(false);
  const [addText, setAddText] = React.useState("");
  const [addBusy, setAddBusy] = React.useState(false);
  // True while the backend is still extracting this patient's episodes (queued
  // or in-flight). Derived from server state (see pollStatusUntilIdle and the
  // mount-check effect), so it survives a page refresh — the "adding…"
  // indicator is restored from GET /memory/{id}/status, not client memory.
  const [addPending, setAddPending] = React.useState(false);
  // Set when the add-poll timed out without the new fact appearing: extraction
  // may have failed server-side, so surface it instead of silently clearing
  // the "adding…" indicator (the entry might also still land — offer a retry).
  const [addTimedOut, setAddTimedOut] = React.useState(false);
  const addPollTimer = React.useRef<number | null>(null);

  const load = React.useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getPatientFacts(patientId, filter);
      setFacts((res.data?.facts ?? []) as MemoryFact[]);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [patientId, filter]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Latest-load ref: lets pollStatusUntilIdle (stable across filter changes)
  // refetch with the user's current filter when the queue drains.
  const loadRef = React.useRef(load);
  React.useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const stopAddPolling = React.useCallback(() => {
    if (addPollTimer.current !== null) {
      window.clearTimeout(addPollTimer.current);
      addPollTimer.current = null;
    }
  }, []);

  // Cancel any in-flight add-poll when the patient changes or on unmount, so a
  // stale poll can't overwrite the new patient's list or leave the indicator on.
  React.useEffect(() => {
    return () => {
      stopAddPolling();
      setAddPending(false);
    };
  }, [patientId, stopAddPolling]);

  const startEdit = (f: MemoryFact) => {
    setRowError(null);
    setEditingUuid(f.uuid ?? null);
    setEditText(f.fact);
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setEditText("");
  };

  const saveEdit = async (f: MemoryFact) => {
    if (!patientId || !f.uuid) return;
    const next = editText.trim();
    if (!next || next === f.fact) {
      cancelEdit();
      return;
    }
    setBusyUuid(f.uuid);
    setRowError(null);
    try {
      const res = await updatePatientFact(patientId, f.uuid, next);
      const updated = res.data as MemoryFact;
      setFacts((prev) => prev.map((x) => (x.uuid === f.uuid ? { ...x, ...updated } : x)));
      cancelEdit();
    } catch {
      setRowError(t("facts.actionError"));
    } finally {
      setBusyUuid(null);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, f: MemoryFact) => {
    setMenuAnchor(e.currentTarget);
    setMenuFact(f);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuFact(null);
  };

  const doRestore = async (f: MemoryFact) => {
    if (!patientId || !f.uuid) return;
    setBusyUuid(f.uuid);
    setRowError(null);
    try {
      const res = await restorePatientFact(patientId, f.uuid);
      const updated = res.data as MemoryFact;
      // If we're viewing only invalidated facts, a restored fact drops out of view.
      if (filter === "invalid") {
        setFacts((prev) => prev.filter((x) => x.uuid !== f.uuid));
      } else {
        setFacts((prev) => prev.map((x) => (x.uuid === f.uuid ? { ...x, ...updated } : x)));
      }
    } catch {
      setRowError(t("facts.actionError"));
    } finally {
      setBusyUuid(null);
    }
  };

  const runConfirm = async () => {
    if (!confirm || !patientId || !confirm.fact.uuid) return;
    const { kind, fact } = confirm;
    setBusyUuid(fact.uuid);
    setRowError(null);
    try {
      if (kind === "delete") {
        await deletePatientFact(patientId, fact.uuid);
        setFacts((prev) => prev.filter((x) => x.uuid !== fact.uuid));
      } else {
        const res = await invalidatePatientFact(patientId, fact.uuid);
        const updated = res.data as MemoryFact;
        if (filter === "active") {
          setFacts((prev) => prev.filter((x) => x.uuid !== fact.uuid));
        } else {
          setFacts((prev) => prev.map((x) => (x.uuid === fact.uuid ? { ...x, ...updated } : x)));
        }
      }
      setConfirm(null);
    } catch {
      setRowError(t("facts.actionError"));
    } finally {
      setBusyUuid(null);
    }
  };

  // Extraction runs asynchronously on the backend (typically ~30–60s, and a
  // single entry can yield several facts). Rather than diffing the fact list
  // against an in-memory baseline (which dies with the page on refresh), poll
  // the server's own extraction state: keep the "adding…" indicator up while
  // episodes are queued/extracting for this patient, and when the queue drains
  // refetch the list once so new facts appear without a manual refresh.
  const pollStatusUntilIdle = React.useCallback(
    (userSubmitted: boolean) => {
      if (!patientId) return;
      const startedAt = Date.now();
      const MAX_MS = 120000;
      const INTERVAL_MS = 3000;

      const tick = async () => {
        let pending = 0;
        try {
          const res = await getPatientMemoryStatus(patientId);
          pending = Number((res.data as { episodes_pending?: number } | undefined)?.episodes_pending ?? 0);
        } catch {
          // Transient fetch error — keep polling until the timeout below.
        }
        if (pending <= 0) {
          setAddPending(false);
          addPollTimer.current = null;
          loadRef.current();
          return;
        }
        if (Date.now() - startedAt >= MAX_MS) {
          setAddPending(false);
          // Only warn when this tab submitted the entry: on refresh-mid-add
          // (or chat-originated episodes) a timeout banner would be noise.
          if (userSubmitted) setAddTimedOut(true);
          addPollTimer.current = null;
          return;
        }
        addPollTimer.current = window.setTimeout(tick, INTERVAL_MS);
      };

      addPollTimer.current = window.setTimeout(tick, INTERVAL_MS);
    },
    [patientId]
  );

  // Refresh continuity: if extraction is already running for this patient
  // (page refreshed mid-add, a submission from another tab, or chat episodes
  // still processing), pick the "adding…" indicator back up from server state.
  React.useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPatientMemoryStatus(patientId);
        if (cancelled) return;
        if (Number((res.data as { episodes_pending?: number } | undefined)?.episodes_pending ?? 0) > 0) {
          setAddPending(true);
          stopAddPolling();
          pollStatusUntilIdle(false);
        }
      } catch {
        // Status endpoint unreachable — skip the indicator; the list load
        // still works via load().
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, pollStatusUntilIdle, stopAddPolling]);

  const submitAdd = async () => {
    if (!patientId) return;
    const text = addText.trim();
    if (!text) return;
    setAddBusy(true);
    try {
      await addPatientFact(patientId, text);
      setAddOpen(false);
      setAddText("");
      setAddTimedOut(false);
      setAddPending(true);
      stopAddPolling();
      pollStatusUntilIdle(true);
    } catch {
      setRowError(t("facts.actionError"));
    } finally {
      setAddBusy(false);
    }
  };

  const filters: FactStatusFilter[] = ["all", "active", "invalid"];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, px: 1, pt: 1 }}>
      <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0, mb: 0.5 }}>
        {t("facts.title")}
      </Typography>
      {/* Header: filter + add */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 11,
              fontWeight: 600,
              py: 0.25,
              px: 1,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {filters.map((f) => (
            <ToggleButton key={f} value={f}>
              {t(`facts.filter.${f}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddText("");
            setAddOpen(true);
          }}
          sx={{ textTransform: "none", fontSize: 11, fontWeight: 600 }}
        >
          {t("facts.add")}
        </Button>
      </Box>

      {addPending && (
        <Alert
          severity="info"
          icon={<CircularProgress size={14} />}
          sx={{ mb: 1, py: 0, fontSize: 11, "& .MuiAlert-icon": { alignItems: "center" } }}
        >
          {t("facts.adding")}
        </Alert>
      )}

      {addTimedOut && !addPending && (
        <Alert
          severity="warning"
          sx={{ mb: 1, py: 0, fontSize: 11, "& .MuiAlert-action": { alignItems: "center", pt: 0 } }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setAddTimedOut(false);
                load();
              }}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 600 }}
            >
              {t("facts.retry")}
            </Button>
          }
        >
          {t("facts.addTimeout")}
        </Alert>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, "&::-webkit-scrollbar": { display: "none" } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : loadError ? (
          <Box sx={{ px: 1, pt: 2 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>{t("facts.loadError")}</Typography>
            <Button size="small" onClick={load} sx={{ textTransform: "none" }}>
              {t("facts.retry")}
            </Button>
          </Box>
        ) : facts.length === 0 ? (
          <Typography sx={{ px: 1, pt: 2, fontSize: 12, color: "text.secondary" }}>
            {filter === "all" ? t("facts.empty") : t("facts.emptyFiltered")}
          </Typography>
        ) : (
          facts.map((f, idx) => {
            const invalid = isInvalidated(f);
            const key = f.uuid ?? `fact-${idx}`;
            const isEditing = editingUuid && f.uuid === editingUuid;
            const isBusy = busyUuid && f.uuid === busyUuid;
            const hasSource = (f.sources?.length ?? 0) > 0;
            return (
              <Box
                key={key}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  p: 1,
                  mb: 1,
                  opacity: invalid ? 0.6 : 1,
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {f.name && (
                      <Chip
                        label={f.name}
                        size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600, mb: 0.5, "& .MuiChip-label": { px: 0.75 } }}
                      />
                    )}
                    {invalid && (
                      <Chip
                        label={t("facts.invalidated")}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600, mb: 0.5, ml: 0.5, "& .MuiChip-label": { px: 0.75 } }}
                      />
                    )}
                    {isEditing ? (
                      <Box sx={{ mt: 0.5 }}>
                        <TextField
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          autoFocus
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                          <IconButton size="small" color="primary" disabled={!!isBusy} onClick={() => saveEdit(f)}>
                            {isBusy ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" disabled={!!isBusy} onClick={cancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.4,
                          textDecoration: invalid ? "line-through" : "none",
                          wordBreak: "break-word",
                        }}
                      >
                        {f.fact}
                      </Typography>
                    )}

                    {!isEditing && (f.valid_at || hasSource) && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                        {f.valid_at && (
                          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                            {formatDate(f.valid_at)}
                            {f.invalid_at ? ` → ${formatDate(f.invalid_at)}` : ""}
                          </Typography>
                        )}
                        {hasSource && (
                          <Button
                            size="small"
                            onClick={() => setOpenSourceUuid(openSourceUuid === f.uuid ? null : (f.uuid ?? null))}
                            sx={{ minWidth: 0, p: 0, fontSize: 10, textTransform: "none" }}
                          >
                            {t("facts.source")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>

                  {!isEditing && (
                    <Box sx={{ display: "flex", flexShrink: 0 }}>
                      {f.uuid && (
                        <>
                          <Tooltip title={t("facts.edit")}>
                            <span>
                              <IconButton size="small" disabled={!!isBusy} onClick={() => startEdit(f)}>
                                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" disabled={!!isBusy} onClick={(e) => openMenu(e, f)}>
                            {isBusy ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </>
                      )}
                    </Box>
                  )}
                </Box>

                <Collapse in={openSourceUuid === f.uuid && hasSource} unmountOnExit>
                  <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${theme.palette.divider}` }}>
                    {(f.sources ?? []).map((s, i) => {
                      const canJump = Boolean(s.conversation_id && s.message_id);
                      return (
                        <Typography
                          key={i}
                          onClick={canJump ? () => jumpToSource(s) : undefined}
                          sx={{
                            fontSize: 11,
                            color: canJump ? "primary.main" : "text.secondary",
                            fontStyle: "italic",
                            mb: 0.5,
                            ...(canJump && {
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "flex-start",
                              gap: 0.25,
                              "&:hover": { textDecoration: "underline" },
                            }),
                          }}
                        >
                          “{s.content}”
                          {canJump && <NorthEastIcon sx={{ fontSize: 12, flexShrink: 0, mt: "1px" }} />}
                        </Typography>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}

        {rowError && (
          <Alert severity="error" sx={{ mt: 1, fontSize: 11, py: 0 }} onClose={() => setRowError(null)}>
            {rowError}
          </Alert>
        )}

        {!loading && !loadError && facts.length > 0 && (
          <Typography sx={{ fontSize: 10, color: "text.secondary", px: 0.5, pb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 12 }} /> {t("facts.note")}
          </Typography>
        )}
      </Box>

      {/* Row action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuFact && isInvalidated(menuFact)
          ? [
              <MenuItem
                key="restore"
                onClick={() => {
                  const f = menuFact;
                  closeMenu();
                  if (f) doRestore(f);
                }}
              >
                {t("facts.action.restore")}
              </MenuItem>,
            ]
          : [
              <MenuItem
                key="invalidate"
                onClick={() => {
                  const f = menuFact;
                  closeMenu();
                  if (f) setConfirm({ kind: "invalidate", fact: f });
                }}
              >
                {t("facts.action.invalidate")}
              </MenuItem>,
            ]}
        <MenuItem
          key="delete"
          sx={{ color: "error.main" }}
          onClick={() => {
            const f = menuFact;
            closeMenu();
            if (f) setConfirm({ kind: "delete", fact: f });
          }}
        >
          {t("facts.action.delete")}
        </MenuItem>
      </Menu>

      {/* Confirmation dialog (invalidate / delete) */}
      <ConfirmDialog
        open={Boolean(confirm)}
        variant={confirm?.kind === "delete" ? "destructive" : "warning"}
        title={confirm?.kind === "delete" ? t("facts.deleteTitle") : t("facts.invalidateTitle")}
        body={confirm?.kind === "delete" ? t("facts.deleteBody") : t("facts.invalidateBody")}
        confirmLabel={confirm?.kind === "delete" ? t("facts.deleteConfirm") : t("facts.invalidateConfirm")}
        cancelLabel={t("facts.cancel")}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
        loading={Boolean(busyUuid)}
      >
        {confirm && (
          <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
            <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>“{confirm.fact.fact}”</Typography>
          </Box>
        )}
      </ConfirmDialog>

      {/* Add-fact dialog */}
      <Dialog open={addOpen} onClose={() => (addBusy ? null : setAddOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>{t("facts.addTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            placeholder={t("facts.addPlaceholder")}
            fullWidth
            multiline
            minRows={2}
            autoFocus
            sx={{ mt: 1 }}
          />
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13 }} /> {t("facts.addHint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={addBusy} sx={{ textTransform: "none" }}>
            {t("facts.cancel")}
          </Button>
          <Button onClick={submitAdd} disabled={addBusy || !addText.trim()} variant="contained" sx={{ textTransform: "none" }}>
            {addBusy ? <CircularProgress size={18} /> : t("facts.addSubmit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
